<template>
  <div class="acc-meal">
    <div class="acc-meal__top">
      <div class="acc-meal__left">
        <div class="acc-meal__id">
          {{ orderNumber }}.
          <router-link
            v-if="reservation.reservation_hotel && !isNameLinkDisable"
            :to="{ name: 'reservation-hotels-general', params: { id: reservation.reservation_hotel } }"
            class="acc-meal__link"
          >
            Reservation #ATH - {{ reservation.reservation_hotel }}
          </router-link>
          <span v-if="reservation.reservation_hotel && isNameLinkDisable">
            Reservation #ATH - {{ reservation.reservation_hotel }}
          </span>
          <router-link
            v-if="!reservation.reservation_hotel && !isNameLinkDisable"
            :to="{ name: 'reservation-meal-general', params: { id: reservation.id } }"
            class="acc-meal__link"
          >
            Reservation #ATML - {{ reservation.id }}
          </router-link>
          <span v-if="!reservation.reservation_hotel && isNameLinkDisable">
            Reservation #ATML - {{ reservation.id }}
          </span>
          <div v-if="showMealContext" class="acc-meal__source">
            {{ reservation.reservation_hotel ? `Part of hotel reservation #${reservation.reservation_hotel}` : 'Standalone meal reservation' }}
          </div>
        </div>
        <div class="acc-meal__tower">
          <div class="acc-meal__name-wrapper">
            <div class="acc-meal__name">{{ reservation.hotel ? reservation.hotel.name : 'Hotel not specified' }}</div>
            <span
              v-if="!reservation.clients_assignment && reservation.reservation_status.code === 'booked' && !reservation.reservation_hotel"
              :style="{ 'backgroundColor': '#439ad1' }"
              class="acc-meal__status"
            >
              Missing clients assignment
            </span>
            <span
              v-if="countdown"
              :style="{ 'backgroundColor': calculateTimeDiff(countdown).difference < 360 ? '#77b017' : calculateTimeDiff(countdown).difference < 1440 ? '#f0c427' : '#e85c20' }"
              class="acc-meal__status"
            >
              {{ calculateTimeDiff(countdown).time_string }}
            </span>
          </div>
          <div class="acc-meal__timing">
            <div class="acc-meal__time">
              <div class="acc-meal__time-text">Date:</div>
              <div class="acc-meal__time-value">
                <template v-if="reservation.min_date !== reservation.max_date">
                  {{ dateFormat(reservation.min_date) }} - {{ dateFormat(reservation.max_date) }}
                </template>
                <template v-else>{{ dateFormat(reservation.min_date) }}</template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="warning" class="acc-meal__warning" role="status">{{ warning }}</div>
    <button class="acc-meal__button" :class="{ 'acc-meal__button--context': showMealContext }" type="button" :aria-expanded="is_acc_open ? 'true' : 'false'" @click="is_acc_open = !is_acc_open">
      <span class="acc-meal__status-block">
        <span
          v-if="reservation.lead && reservation.lead.reservation_manager"
          v-tooltip="{ content: reservation.lead.reservation_manager.full_name, placement: 'right', offset: 10 }"
          class="acc-meal__photo"
        >
          <img :src="getUserAvatar(reservation.lead.reservation_manager.crop.crop_url)" alt="Avatar">
        </span>
        <span v-else class="acc-meal__photo">
          <img alt="Avatar" src="@/assets/img/temp/avatar-slug.jpg">
        </span>
        <span :style="{ 'backgroundColor': reservation.reservation_status.color || '#000'  }" class="acc-meal__status">
          {{ showMealContext ? `Reservation: ${reservationStatusLabel}` : reservation.reservation_status.name }}
        </span>
      </span>
      <span
        v-tooltip="{ content: reservation.lead ? reservation.lead.manager.full_name : '', placement: 'top-start', offset: 10 }"
        class="acc-meal__photo"
      >
        <img :src="getUserAvatar(reservation.lead ? reservation.lead.manager.crop.crop_url : '')" alt="Avatar">
      </span>
      <span v-if="reservation.sales_status" :style="{ 'backgroundColor': reservation.sales_status.color || '#000'  }"
            class="acc-meal__status">
        {{ showMealContext ? `Sales: ${reservation.sales_status.name}` : reservation.sales_status.name }}
      </span>
      <img v-if="reservation.notes" alt="" src="@/assets/img/transfer-notes-icon.svg">
      <span v-if="reservation.lead" class="acc-meal__time">
        <span class="acc-meal__time-text">Client:</span>
        <span class="acc-meal__name">
        {{ reservation.lead.name ? reservation.lead.name : `Lead #${ reservation.lead?.id }` }}
        </span>
      </span>
      <span :class="{ 'acc-meal__toggler--open': is_acc_open }" class="acc-meal__toggler">
        <img alt="" src="@/assets/img/arrow-toggler-bottom-icon.svg">
      </span>
    </button>
    <transition name="slide-block">
      <div v-if="is_acc_open" class="acc-meal__dropdown">
        <div class="acc-meal__list">
          <AppMealCard
            v-for="(card, index) in reservation.requests"
            :key="`meal_card_${card.id}`"
            :order-number="index + 1"
            :meal="card"
            :is-deletable="false"
          />
        </div>
        <div v-if="reservation.notes" class="acc-meal__notes">
          <div class="acc-meal__note-icon">
            <img alt="" src="@/assets/img/note-green-big-icon.svg">
            <span>NOTE (by manager):</span>
          </div>
          <div class="acc-meal__text">{{ reservation.notes }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { differenceInMinutes, format, parse } from 'date-fns'
import AppMealCard from '@/components/AppMealCard.vue'

export default {
  name: 'AppAccordionMeal',
  components: { AppMealCard },
  props: {
    reservation: {
      type: Object,
      required: true,
    },
    orderNumber: {
      type: [Number, String],
      default: 0
    },
    countdown: {
      type: String,
      default: ''
    },
    isNameLinkDisable: {
      type: Boolean,
      default: false
    },
    showMealContext: {
      type: Boolean,
      default: false
    },
    warning: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      is_acc_open: false
    }
  },
  computed: {
    reservationStatusLabel() {
      const labels = {
        new: 'Awaiting confirmation',
        booked: 'Confirmed',
        not_available: 'Not available',
        cancelled: 'Cancelled'
      }
      return labels[this.reservation.reservation_status.code] || this.reservation.reservation_status.name
    }
  },
  methods: {
    calculateTimeDiff(start) {
      const difference = differenceInMinutes(new Date(), new Date(start))
      const minutes = difference % 60
      const hours = Math.trunc((
        difference / 60
      ) % 24)
      const days = Math.trunc(difference / 60 / 24)

      return {
        time_string: `${ days ? `${ days }d ` : '' }${ hours ? `${ hours }h ` : '' }${ minutes }m`,
        difference
      }
    },
    dateFormat(date) {
      if (date) return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
      else return '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    }
  }
}
</script>

<style lang="sass" scoped>
.acc-meal
  border: 1px solid #dfdfdf
  border-radius: 10px
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08)
  background-color: #fff
  overflow: hidden

.acc-meal__top
  display: flex
  align-items: center
  justify-content: space-between
  padding: 12px 16px
  border-bottom: 1px solid #e8e8e8

.acc-meal__left
  display: grid
  grid-template-columns: 240px max-content max-content
  align-items: center
  gap: 20px

.acc-meal__id
  font-weight: 500

.acc-meal__source
  margin-top: 6px
  color: #5f5f5f
  font-size: 12px
  font-weight: 400

.acc-meal__warning
  padding: 10px 16px
  background-color: #fff3df
  color: #825000
  border-bottom: 1px solid #eed3a5

.acc-meal__photo
  width: 31px
  line-height: 0
  border-radius: 50%
  overflow: hidden

.acc-meal__link
  display: inline-block
  color: $color-text-base
  font-weight: 500
  transition: color 0.3s

  &:after
    content: ''
    display: block
    height: 2px
    border-radius: 3px
    background-color: #aae182
    transform: translateY(-1px)
    transition: opacity 0.3s

  &:hover
    &:after
      opacity: 0

.acc-meal__name-wrapper
  display: flex
  gap: 10px
  align-items: center
  margin-bottom: 6px

.acc-meal__name
  font-weight: 500

.acc-meal__nights
  color: #5f5f5f

.acc-meal__button
  position: relative
  display: grid
  grid-template-columns: 240px 31px max-content max-content max-content
  align-items: center
  gap: 20px
  width: 100%
  padding: 10px 16px
  cursor: pointer
  text-align: left

.acc-meal__status-block
  display: inline-flex
  align-items: center
  gap: 12px

.acc-meal__button--context
  display: flex
  flex-wrap: wrap
  padding-right: 48px

  .acc-meal__status
    min-width: 0

.acc-meal__status
  display: inline-block
  min-width: 110px
  padding: 4px 8px 5px
  border-radius: 20px
  color: #fff
  font-size: 13px
  font-weight: 600
  text-align: center

.acc-meal__timing
  display: flex
  align-items: center
  gap: 20px

.acc-meal__time
  display: flex
  gap: 6px

.acc-meal__time-text
  color: #5f5f5f

.acc-meal__time-value
  color: #181818
  font-weight: 500

.acc-meal__toggler
  position: absolute
  top: 14px
  right: 18px
  transition: transform 0.3s

.acc-meal__toggler--open
  transform: rotate(180deg)

.acc-meal__dropdown
  border-top: 1px solid #e8e8e8
  background-color: #fafafa

.acc-meal__list
  padding: 14px

.acc-meal__room
  margin-bottom: 10px

  &:last-child
    margin-bottom: 0

.acc-meal__notes
  display: flex
  align-items: center
  gap: 8px
  padding: 12px 18px 10px
  border-top: 1px solid #e8e8e8

.acc-meal__note-icon
  flex-shrink: 0
  display: flex
  align-items: center
  gap: 10px
  color: #181818
  font-weight: 500

.acc-meal__text
  color: #5f5f5f

.acc-meal__right
  display: flex
  align-items: center
  gap: 10px

// ------------------------ slide-block
.slide-block-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-block-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-block-enter-to, .slide-block-leave
  max-height: 500px
  overflow: hidden

.slide-block-enter, .slide-block-leave-to
  overflow: hidden
  max-height: 0
</style>
